/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { ClinicalDepartmentEnum } from '../models/clinical-department-enum';
import { GenericResponse } from '../models/generic-response';
import { LegacyImportResponse } from '../models/legacy-import-response';

/**
 * Import legacy data from Excel files. Each row becomes a form submission. Supports multiple legacy source systems with traceability columns (legacy_source, legacy_record_id). Imported rows appear in the normal submission lists.
 */
@Injectable({ providedIn: 'root' })
export class LegacyImportService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `importLegacyExcel()` */
  static readonly ImportLegacyExcelPath =
    '/api/v1/clinical/{department}/import';

  /**
   * Import legacy data from a multi-sheet Excel file.
   *
   * Upload an Excel (.xlsx) file produced by the template download endpoint. Each sheet name must match a valid form type for the department (e.g. ROTOR CHART_REVIEW, FIXED EPCR_CHART_REVIEW, OPERATIONS FW_FLIGHT_REQUESTED). Every sheet is processed independently; sheets with unrecognised names are silently skipped. The response contains one summary per sheet processed. Requires LEGACY_IMPORT_ROTOR, LEGACY_IMPORT_FIXED, LEGACY_IMPORT_OPERATIONS, or LEGACY_IMPORT_CLINICAL_DEPARTMENT for the target department.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `importLegacyExcel()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  importLegacyExcel$Response(
    params: {
      department: ClinicalDepartmentEnum;
      body: {
        /**
         * Excel (.xlsx) file with legacy data
         */
        file: Blob;
      };
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<LegacyImportResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      LegacyImportService.ImportLegacyExcelPath,
      'post'
    );
    if (params) {
      rb.path('department', params.department, {});
      rb.body(params.body, 'multipart/form-data');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<LegacyImportResponse>;
        })
      );
  }

  /**
   * Import legacy data from a multi-sheet Excel file.
   *
   * Upload an Excel (.xlsx) file produced by the template download endpoint. Each sheet name must match a valid form type for the department (e.g. ROTOR CHART_REVIEW, FIXED EPCR_CHART_REVIEW, OPERATIONS FW_FLIGHT_REQUESTED). Every sheet is processed independently; sheets with unrecognised names are silently skipped. The response contains one summary per sheet processed. Requires LEGACY_IMPORT_ROTOR, LEGACY_IMPORT_FIXED, LEGACY_IMPORT_OPERATIONS, or LEGACY_IMPORT_CLINICAL_DEPARTMENT for the target department.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `importLegacyExcel$Response()` instead.
   *
   * This method sends `multipart/form-data` and handles request body of type `multipart/form-data`.
   */
  importLegacyExcel(
    params: {
      department: ClinicalDepartmentEnum;
      body: {
        /**
         * Excel (.xlsx) file with legacy data
         */
        file: Blob;
      };
    },
    context?: HttpContext
  ): Observable<LegacyImportResponse> {
    return this.importLegacyExcel$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<LegacyImportResponse>): LegacyImportResponse =>
          r.body
      )
    );
  }

  /** Path part for operation `submitBatchToWms()` */
  static readonly SubmitBatchToWmsPath =
    '/api/v1/clinical/import/{batchId}/submit-to-wms';

  /**
   * Submit imported batch to WMS approval workflow.
   *
   * Finds all SUBMITTED rows in the given import batch and creates WMS workflow instances for eligible form types (ROTOR CHART_REVIEW, FIXED EPCR_CHART_REVIEW, CLINICAL_DEPARTMENT POST_FLIGHT_CLINICAL, CLINICAL_DEPARTMENT AIRWAY_REGISTARY_2026, and all Operations forms integrated with WMS). Other form types and APPROVED/DRAFT rows are skipped. Rows that already have a workflowInstanceId are also skipped.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `submitBatchToWms()` instead.
   *
   * This method doesn't expect any request body.
   */
  submitBatchToWms$Response(
    params: {
      batchId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      LegacyImportService.SubmitBatchToWmsPath,
      'post'
    );
    if (params) {
      rb.path('batchId', params.batchId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Submit imported batch to WMS approval workflow.
   *
   * Finds all SUBMITTED rows in the given import batch and creates WMS workflow instances for eligible form types (ROTOR CHART_REVIEW, FIXED EPCR_CHART_REVIEW, CLINICAL_DEPARTMENT POST_FLIGHT_CLINICAL, CLINICAL_DEPARTMENT AIRWAY_REGISTARY_2026, and all Operations forms integrated with WMS). Other form types and APPROVED/DRAFT rows are skipped. Rows that already have a workflowInstanceId are also skipped.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `submitBatchToWms$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  submitBatchToWms(
    params: {
      batchId: string;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.submitBatchToWms$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `rollbackImportBatch()` */
  static readonly RollbackImportBatchPath = '/api/v1/clinical/import/{batchId}';

  /**
   * Rollback (undo) an entire import batch.
   *
   * For each imported row that has a WMS clinical workflow instance id, calls WMS full cancellation first (same instances created by submit-to-wms), then permanently deletes all form submissions and their step entities for the given batchId. If any required WMS cancellation fails, the operation aborts and no clinical rows are removed. Use when an import had wrong data and needs to be re-uploaded. The batchId is returned in every import response.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `rollbackImportBatch()` instead.
   *
   * This method doesn't expect any request body.
   */
  rollbackImportBatch$Response(
    params: {
      batchId: string;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      LegacyImportService.RollbackImportBatchPath,
      'delete'
    );
    if (params) {
      rb.path('batchId', params.batchId, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * Rollback (undo) an entire import batch.
   *
   * For each imported row that has a WMS clinical workflow instance id, calls WMS full cancellation first (same instances created by submit-to-wms), then permanently deletes all form submissions and their step entities for the given batchId. If any required WMS cancellation fails, the operation aborts and no clinical rows are removed. Use when an import had wrong data and needs to be re-uploaded. The batchId is returned in every import response.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `rollbackImportBatch$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  rollbackImportBatch(
    params: {
      batchId: string;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.rollbackImportBatch$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `downloadImportTemplate()` */
  static readonly DownloadImportTemplatePath =
    '/api/v1/clinical/{department}/import/template';

  /**
   * Download the Excel import template for a department.
   *
   * Returns a .xlsx file with one sheet per form type for the given department (e.g. ROTOR returns sheets CHART_REVIEW, SHIFT_FATIGUE_ASSESSMENT, POST_FLIGHT_DEBRIEF; OPERATIONS returns all eight operations form types such as FW_FLIGHT_REQUESTED and EPCR_TIMING; CLINICAL_DEPARTMENT returns POST_FLIGHT_CLINICAL and AIRWAY_REGISTARY_2026). Each sheet has Row 1 = field keys (used during import), Row 2 = human-readable labels. Fixed meta-columns: legacy_source, legacy_record_id, status, approved_by_email, approved_at, approval_message. Remaining columns are the form's field keys.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `downloadImportTemplate()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadImportTemplate$Response(
    params: {
      department: ClinicalDepartmentEnum;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Blob>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      LegacyImportService.DownloadImportTemplatePath,
      'get'
    );
    if (params) {
      rb.path('department', params.department, {});
    }

    return this.http
      .request(
        rb.build({
          responseType: 'blob',
          accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          context,
        })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<Blob>;
        })
      );
  }

  /**
   * Download the Excel import template for a department.
   *
   * Returns a .xlsx file with one sheet per form type for the given department (e.g. ROTOR returns sheets CHART_REVIEW, SHIFT_FATIGUE_ASSESSMENT, POST_FLIGHT_DEBRIEF; OPERATIONS returns all eight operations form types such as FW_FLIGHT_REQUESTED and EPCR_TIMING; CLINICAL_DEPARTMENT returns POST_FLIGHT_CLINICAL and AIRWAY_REGISTARY_2026). Each sheet has Row 1 = field keys (used during import), Row 2 = human-readable labels. Fixed meta-columns: legacy_source, legacy_record_id, status, approved_by_email, approved_at, approval_message. Remaining columns are the form's field keys.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `downloadImportTemplate$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  downloadImportTemplate(
    params: {
      department: ClinicalDepartmentEnum;
    },
    context?: HttpContext
  ): Observable<Blob> {
    return this.downloadImportTemplate$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }
}
